import { useId, useRef, useState } from 'react';
import i18n from 'helpers/i18n';
import messages from './messages';
import sharedMessages from '../messages';
import RouteHelpers from 'helpers/routes';
import Spacer from 'components/Spacer';
import { FormikHelpers } from 'formik';
import { Link } from 'react-router-dom';
import { useSelector } from 'react-redux';
import { getValidationSchema } from './utils';
import { Button, TextField, TextLink } from '@teachstonellc/component-library';
import { useLoginManager } from 'components/LoginManager';
import { Formik, Form, PhoneInput } from 'components/EnhancedForm';
import VisibilityToggle from 'components/VisibilityToggle';
import FocusFirstInvalidField from 'components/FocusFirstInvalidField';
import useFocusOnStepChange from '../useFocusOnStepChange';

import {
  formatLoginIdentifier,
  getDefaultPhoneCountry,
  isEmailLike,
  joinIds,
  normalizeLoginInput
} from '../loginInput';

import {
  ButtonsWrapper,
  ButtonWrapper,
  TextButtonWrapper,
  ForgotWrapper
} from './Styled';

interface LoginWithPasswordProps {
  /** If `true`, the form will display phone + password fields. Otherwise email + password. */
  loginWithPhone?: boolean;
}

function LoginWithPassword({ loginWithPhone }: LoginWithPasswordProps) {
  const [isPasswordHidden, setIsPasswordHidden] = useState(true);
  const { setStep, setEmailOrPhone, emailOrPhone, submitForm } =
    useLoginManager();
  const emailOrPhoneInputRef = useRef<HTMLInputElement>(null);
  const phoneInputRef = useRef<HTMLInputElement>(null);
  const passwordInputRef = useRef<HTMLInputElement>(null);
  useFocusOnStepChange(passwordInputRef);
  const signingInAsId = useId();
  const passwordErrorId = useId();

  // The locale is used to update the user's preferred language on login.
  // This is old functionallity, we might want to revisit in the future.
  const locale = useSelector((state: any) => state.locale);

  function handleSubmit(values: any, formik: FormikHelpers<any>) {
    let data: any = {
      user: { password: values.password, preferred_language: locale }
    };

    if (loginWithPhone) {
      data.user.login_phone = values.emailOrPhone;
    } else {
      data.user.email = normalizeLoginInput(values.emailOrPhone);
    }

    submitForm(data, formik);
  }

  function getForgotPasswordState(value: string) {
    const email = normalizeLoginInput(value);
    return !loginWithPhone && isEmailLike(email) ? { email } : undefined;
  }

  function handleBackToLogin(emailOrPhone: string) {
    return () => {
      setEmailOrPhone(emailOrPhone);
      setStep('login');
    };
  }

  return (
    <Formik
      onSubmit={handleSubmit}
      validationSchema={getValidationSchema(loginWithPhone)}
      initialValues={{ emailOrPhone, password: '' }}
      validateOnBlur={false}
    >
      {formik => {
        const hasSubmitted = formik.submitCount > 0;
        const isEmailOrPhoneError = Boolean(
          hasSubmitted && formik.errors.emailOrPhone
        );
        const isPasswordError = Boolean(hasSubmitted && formik.errors.password);

        const fields = (
          <>
            <FocusFirstInvalidField
              submitCount={formik.submitCount}
              fields={
                loginWithPhone
                  ? [
                      {
                        isInvalid: isEmailOrPhoneError,
                        inputRef: phoneInputRef
                      },
                      {
                        isInvalid: isPasswordError,
                        inputRef: passwordInputRef
                      }
                    ]
                  : [
                      {
                        isInvalid: isEmailOrPhoneError,
                        inputRef: emailOrPhoneInputRef
                      },
                      {
                        isInvalid: isPasswordError,
                        inputRef: passwordInputRef
                      }
                    ]
              }
            />

            {loginWithPhone ? (
              <PhoneInput
                name="emailOrPhone"
                inputRef={phoneInputRef}
                defaultCountry={getDefaultPhoneCountry()}
                label={i18n.ft(messages.fields.phone)}
                placeholder={i18n.ft(messages.fields.phonePlaceholder)}
              />
            ) : (
              <TextField
                {...formik.getFieldProps('emailOrPhone')}
                ref={emailOrPhoneInputRef}
                type="email"
                autoComplete="username"
                label={i18n.ft(messages.fields.email)}
                placeholder={i18n.ft(messages.fields.emailPlaceholder)}
                aria-invalid={isEmailOrPhoneError}
                error={
                  isEmailOrPhoneError ? formik.errors.emailOrPhone : undefined
                }
              />
            )}

            <span id={signingInAsId} className="sr-only">
              {i18n.ft(messages.signingInAs, {
                identifier: formatLoginIdentifier(formik.values.emailOrPhone)
              })}
            </span>

            <Spacer size={8} />
            <div
              className={`flex gap-1 ${
                isPasswordError ? 'items-start' : 'items-end'
              }`}
            >
              <div className="flex-1">
                <TextField
                  {...formik.getFieldProps('password')}
                  ref={passwordInputRef}
                  type={isPasswordHidden ? 'password' : 'text'}
                  label={i18n.ft(messages.fields.password)}
                  placeholder={i18n.ft(messages.fields.passwordPlaceholder)}
                  aria-invalid={isPasswordError}
                  errorId={passwordErrorId}
                  aria-describedby={joinIds(
                    signingInAsId,
                    isPasswordError && passwordErrorId
                  )}
                  error={isPasswordError ? formik.errors.password : undefined}
                />
              </div>

              <div className={isPasswordError ? 'mt-[28px]' : undefined}>
                <VisibilityToggle
                  isShown={!isPasswordHidden}
                  showLabel={i18n.ft(messages.showPassword)}
                  hideLabel={i18n.ft(messages.hidePassword)}
                  onToggle={() => setIsPasswordHidden(prev => !prev)}
                />
              </div>
            </div>

            <ButtonsWrapper>
              <ButtonWrapper>
                <Button
                  fullWidth
                  variant="primary"
                  shape="rounded"
                  type="submit"
                  loading={formik.isSubmitting}
                  disabled={formik.isSubmitting}
                >
                  {i18n.ft(messages.login)}
                </Button>
              </ButtonWrapper>

              <ForgotWrapper>
                <TextLink
                  as={Link}
                  to={RouteHelpers.getPath('users-forgot-password')}
                  state={getForgotPasswordState(formik.values.emailOrPhone)}
                >
                  {i18n.ft(messages.forgotPassword)}
                </TextLink>
              </ForgotWrapper>
            </ButtonsWrapper>

            <TextButtonWrapper>
              <TextLink
                as="button"
                type="button"
                onClick={handleBackToLogin(formik.values.emailOrPhone)}
              >
                {i18n.ft(sharedMessages.useDifferentLogin)}
              </TextLink>
            </TextButtonWrapper>
          </>
        );

        // `PhoneInput` is a Semantic-UI-based component that relies on
        // `.ui.form`'s CSS for its styling, so it still needs Semantic's
        // <Form>. The TextField-based email/password fields don't want
        // that CSS bleeding onto them, so they get a plain <form> wired
        // directly to Formik's handlers instead.
        return loginWithPhone ? (
          <Form>{fields}</Form>
        ) : (
          <form
            noValidate
            onSubmit={formik.handleSubmit}
            onReset={formik.handleReset}
          >
            {fields}
          </form>
        );
      }}
    </Formik>
  );
}

export default LoginWithPassword;
